import { css, cx } from '@linaria/core'
import { type FC, useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppNavigation } from '#app/navigation/NavigationContext'
import { useAppDispatch } from '#app/store'
import { type RecoverableConfiguration, setConfigurationTitle, type TruckConfiguration } from '#app/store/slices/configurationSlice'
import { Dropdown } from '#components/Dropdown'
import { HoverTooltip } from '#components/HoverTooltip'
import { Icon } from '#components/Icon'
import { Input } from '#components/Input'
import { getConfigurationTitle, UntitledConfigurationPlaceholder } from '#features/Configuration/ConfigurationTitle'
import ChevronDownIcon from '#icons/chevron-1-down-24.svg'
import { makeConfigurationLocation } from '#pages/Configuration/components/ConfigurationNavigation'
import { ConfigurationStatusIcon } from '#pages/Configuration/components/ConfigurationStatusIcon'
import { useDenominationDisplayData } from '#pages/Configuration/components/useDenominationDisplayData'
import { fragments, style } from '#styles'
import { testIds } from '#utils/testIds'
import { ConfigurationDropdownContent } from './ConfigurationHeaderDropdownContent'
import { ConfigurationHeaderHover } from './ConfigurationHeaderHover'

const headerClass = css`
  cursor: pointer;
  max-width: 230px;
  height: 41px;
  padding: 4px 0 10px 0;
  overflow: hidden;
  white-space: nowrap;
  display: flex;

  align-items: center;


  > ._dropdown {
    display: flex;
    align-items: center;
    gap: 4px;
    > ._tab-menu {
      display: flex;
      align-items: center;
    }
    > .tooltip > ._content {
      max-width: unset;
    }


    > ._index, > ._status {
      justify-self: center;
    }

    > ._status {
      color: ${style.theme.textDefault};
      transition: 0.25s ease opacity;

      &._spinner {
        opacity: 1;
      }
    }

    > ._title-combo {
      max-width: 188px;
      flex: 1;
      position: relative;
      white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        


      > span {
        max-width: 188px;
        ${fragments.textStyle.body.l.condensedBold};
      }
      
      > input {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        top: 0;
        height: 100%;
        width: 100%;
        padding: 2px;
        z-index: 1;
        min-height: unset;
        
        ${fragments.textStyle.body.l.condensedBold};
        color: ${style.theme.textDefault};
        border: none;
        outline: 1px solid ${style.theme.borderInput};
        outline-offset: -1px;
        transition: 0.25s ease outline-color;
        opacity: 0;
        pointer-events: none;

        &:focus {
          outline-color: ${style.theme.borderOutline};
          pointer-events: all;
          opacity: 1;
        }

        &::placeholder {
          ${fragments.textStyle.body.m.condensedBold};
        }

        &._input-invisible {
          visibility: hidden;
          display: none;
        }
      }
    }
    

    &._selected {
      > ._title > ._title-editing {
        color: ${style.theme.textAccent};
      }

      > ._status._spinner > .spinner > svg > ._trail {
        stroke: ${style.theme.textAccent};
      }

      > ._status {
        color: ${style.theme.textAccent};
      }
    }
  }
`

const popupClass = css`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  background-color: ${style.theme.backgroundSurfacePrimaryStrong};
  outline: 0;
  max-width: 250px;

  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  padding: 8px 0;

  > ._entry {
    padding: 6px 18px 6px 12px;
  }

  > ._entry {
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    align-self: stretch;

    &:hover {
      background: ${style.theme.backgroundSurfaceStandardSubtle};
    }
  }

  ._info {
    color: ${style.theme.textSoft};
    ${fragments.textStyle.body.xs.regular};
    padding: 10px 18px 0 12px;
  }
`

const isTruckConfiguration = (x: TruckConfiguration | RecoverableConfiguration): x is TruckConfiguration => x.type === 'truck-configuration'

export const ConfigurationHeaderTab: FC<{
  configuration: TruckConfiguration | RecoverableConfiguration
  activeConfiguration?: TruckConfiguration
  allConfigurations: (TruckConfiguration | RecoverableConfiguration)[]
}> = ({ configuration, activeConfiguration, allConfigurations }) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const asTruckConfiguration = isTruckConfiguration(configuration)

  const [localTitle, setLocalTitle] = useState<{ title: string | undefined; changed: boolean }>({
    title: undefined,
    changed: false,
  })

  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const saveAndClose = useCallback(() => {
    if (!asTruckConfiguration) {
      return
    }
    if (localTitle.changed && localTitle.title !== configuration.title) {
      dispatch(
        setConfigurationTitle({
          id: configuration.id,
          title: localTitle.title || UntitledConfigurationPlaceholder,
        }),
      )
    }
    setLocalTitle({
      title: undefined,
      changed: false,
    })
  }, [localTitle, asTruckConfiguration, configuration, configuration.id, dispatch])

  const { navigate } = useAppNavigation()

  const onTabClick = useCallback(() => {
    if (!asTruckConfiguration) {
      return
    }
    const location = makeConfigurationLocation(configuration)
    if (!location) {
      return
    }
    navigate(location)
  }, [navigate, asTruckConfiguration, configuration])

  const { configurationTitle, isDefaultConfigurationTitle } = useDenominationDisplayData(configuration)
  const displayTitle = getConfigurationTitle(configuration.title, configuration.marketDenomination, t)
  const editableTitle = (localTitle.changed ? localTitle.title : undefined) ?? (isDefaultConfigurationTitle ? configurationTitle : '')

  const onKeyDown = useCallback((evt: React.KeyboardEvent<HTMLInputElement>) => {
    if (evt.key === 'Enter') {
      evt.currentTarget.blur()
    }
  }, [])

  const onChange = useCallback((evt: React.FormEvent<HTMLInputElement>) => {
    setLocalTitle({
      title: evt.currentTarget.value,
      changed: true,
    })
  }, [])
  const onBlur = useCallback(
    (evt: React.FocusEvent<HTMLInputElement, Element>) => {
      // for very long titles, scroll to the beginning so that the ellipsis is visible
      evt.currentTarget.setSelectionRange(0, 0)
      saveAndClose()
    },
    [saveAndClose],
  )

  const setIsEditing = useCallback((isEdit: boolean) => {
    if (inputRef.current && isEdit) {
      inputRef.current.focus()
      inputRef.current.setSelectionRange(0, inputRef.current.value.length)
    }
  }, [])

  const [isOpen, setIsOpen] = useState(false)
  const concreteConfigurations = useMemo(() => allConfigurations.filter((x) => x.type === 'truck-configuration'), [allConfigurations])

  return (
    <HoverTooltip.div
      appearance="transparent"
      offset={4}
      className={cx(headerClass, configuration.id === activeConfiguration?.id && '_selected')}
      title={<ConfigurationHeaderHover configuration={configuration} />}
      onClick={onTabClick}
      data-testid={testIds.configuration.navigationTab(configuration.id)}
    >
      <Dropdown
        withoutArrow
        open={isOpen}
        placement="bottom-end"
        offset={12}
        onOpenChange={setIsOpen}
        popupClassName={popupClass}
        className="_dropdown"
        onClick={(evt) => {
          if (activeConfiguration?.id === configuration.id) {
            evt.preventDefault()
            evt.stopPropagation()
            setIsOpen(true)
          }
        }}
        dropdownContent={
          isTruckConfiguration(configuration) && (
            <ConfigurationDropdownContent
              configuration={configuration}
              activeConfiguration={activeConfiguration}
              allConfigurations={concreteConfigurations}
              startEditing={() => setIsEditing(true)}
              close={() => setIsOpen(false)}
            />
          )
        }
        data-testid={testIds.configuration.header.dropdown}
      >
        <ConfigurationStatusIcon isActive={configuration.id === activeConfiguration?.id} configuration={configuration} />
        <span className="_title-combo">
          <Input
            ref={inputRef}
            placeholder={t('shared:configuration.title.placeholder')}
            slim
            value={editableTitle}
            maxLength={40}
            onKeyDown={onKeyDown}
            onChange={onChange}
            onBlur={onBlur}
            data-testid={testIds.configuration.header.titleInput}
          />
          <span className="_title-display" data-testid={testIds.configuration.header.title}>
            {displayTitle}
          </span>
        </span>

        {activeConfiguration?.id === configuration.id && <Icon iconUrl={ChevronDownIcon} className="_icon" size="xxs" />}
      </Dropdown>
    </HoverTooltip.div>
  )
}
